import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Pencil, Trash2, Plus } from 'lucide-react'
import type { MachineProfile } from '../../types'
import { defaultProfile } from '../../hooks/useMachineProfiles'
import MachineProfileEditor from './MachineProfileEditor'

interface MachineProfilesModalProps {
  open: boolean
  profiles: MachineProfile[]
  onAdd: (profile: MachineProfile) => void
  onUpdate: (id: string, updates: Partial<MachineProfile>) => void
  onRemove: (id: string) => void
  onClose: () => void
}

type View = 'list' | 'edit'

export default function MachineProfilesModal({
  open,
  profiles,
  onAdd,
  onUpdate,
  onRemove,
  onClose,
}: MachineProfilesModalProps) {
  const [view, setView] = useState<View>('list')
  const [editing, setEditing] = useState<MachineProfile | null>(null)

  const handleNewProfile = () => {
    setEditing(defaultProfile())
    setView('edit')
  }

  const handleEdit = (profile: MachineProfile) => {
    setEditing({ ...profile })
    setView('edit')
  }

  const handleSave = () => {
    if (!editing) return
    const exists = profiles.some(p => p.id === editing.id)
    if (exists) {
      onUpdate(editing.id, editing)
    } else {
      onAdd(editing)
    }
    setView('list')
    setEditing(null)
  }

  const handleCancel = () => {
    setView('list')
    setEditing(null)
  }

  const handleClose = () => {
    setView('list')
    setEditing(null)
    onClose()
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal card */}
          <motion.div
            key="card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div
              className="glass rounded-2xl p-8 w-full max-w-3xl mx-4 pointer-events-auto max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(6,182,212,0.12)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="font-mono text-xs tracking-[0.3em] uppercase text-cyan-500/60 mb-1">
                    {view === 'list' ? 'MACHINE PROFILES' : editing && profiles.some(p => p.id === editing.id) ? 'EDIT PROFILE' : 'NEW PROFILE'}
                  </p>
                  <h2 className="font-mono font-bold text-xl tracking-tight text-slate-100">
                    {view === 'list' ? 'Machine Profiles' : editing?.name || 'New Machine'}
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  className="text-slate-600 hover:text-slate-300 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <AnimatePresence mode="wait">
                {view === 'list' ? (
                  <motion.div
                    key="list"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.15 }}
                    className="space-y-3"
                  >
                    {profiles.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="font-mono text-sm text-slate-500 mb-2">No machine profiles yet</p>
                        <p className="font-mono text-xs text-slate-600">
                          Create your first machine profile to get started
                        </p>
                      </div>
                    ) : (
                      profiles.map(profile => (
                        <div
                          key={profile.id}
                          className="flex items-center justify-between p-4 rounded-xl border border-cyan-500/15 bg-white/3 hover:border-cyan-500/25 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="font-mono text-sm font-semibold text-slate-200 truncate">
                                {profile.name}
                              </span>
                              <span className="font-mono text-[10px] text-cyan-500/70 bg-cyan-500/10 px-2 py-0.5 rounded shrink-0">
                                {profile.controller}
                              </span>
                              <span className="font-mono text-[10px] text-slate-400 bg-white/5 border border-cyan-500/15 px-2 py-0.5 rounded shrink-0 capitalize">
                                {profile.type}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 font-mono text-[10px] text-slate-600">
                              <span>{profile.travelX}×{profile.travelY}×{profile.travelZ} mm</span>
                              <span>{profile.maxRpm.toLocaleString()} rpm</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4 shrink-0">
                            <button
                              onClick={() => handleEdit(profile)}
                              className="p-1.5 text-slate-500 hover:text-cyan-400 transition-colors"
                              aria-label="Edit profile"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => onRemove(profile.id)}
                              className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                              aria-label="Delete profile"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}

                    {/* New Profile button */}
                    <button
                      onClick={handleNewProfile}
                      className="
                        w-full flex items-center justify-center gap-2
                        font-mono text-xs text-cyan-400 border border-dashed border-cyan-500/30
                        rounded-xl py-3 mt-2
                        hover:border-cyan-400 hover:bg-cyan-500/5 transition-all duration-150
                      "
                    >
                      <Plus className="w-3.5 h-3.5" />
                      New Profile
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="edit"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.15 }}
                  >
                    {editing && (
                      <MachineProfileEditor
                        value={editing}
                        onChange={setEditing}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        existingNames={profiles.filter(p => p.id !== editing.id).map(p => p.name)}
                      />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
